/**
 * «Подписка и тренды» — ДЕМО.
 * Финансовый горизонт владельца: MRR и история денег во времени.
 * Договор 50/50 — разовое; подписка 4900₽ + брэкеты — повторяющаяся выручка.
 * Пока нет dialog_counter и накопленной истории — приглушённый demo-блок с дорожной картой.
 */

import { Repeat, Users, MessagesSquare } from 'lucide-react'
import { ColumnChart, KpiCard, Sparkline } from './primitives'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { DemoBadge } from './primitives'
import { formatRub } from '@/lib/utils'
import type { DashboardTrends } from '@/types'

function monthLabel(ym: string): string {
  const m = ym.split('-')[1]
  const names = ['', 'янв', 'фев', 'мар', 'апр', 'май', 'июн', 'июл', 'авг', 'сен', 'окт', 'ноя', 'дек']
  return names[parseInt(m, 10)] ?? ym
}

function weekLabel(w: string): string {
  const ww = w.split('-W')[1]
  return ww ? `н${ww}` : w
}

export function TrendsDemo({ trends }: { trends: DashboardTrends }) {
  const revData = trends.revenue_by_month.map((r) => ({
    label: monthLabel(r.month),
    value: r.amount,
  }))
  const leadsData = trends.leads_by_week.map((l) => l.count)
  const lastWeek = trends.leads_by_week[trends.leads_by_week.length - 1]

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
        <KpiCard
          label="MRR (месячная регулярная выручка)"
          value={formatRub(trends.mrr)}
          icon={Repeat}
          demo
          demoLabel="демо · появится после первых подписок и счётчика диалогов"
        />
        <KpiCard
          label="Активных подписок"
          value={trends.active_subscriptions}
          icon={Users}
          demo
          demoLabel="демо · появится после запуска тарификации"
        />
        <KpiCard
          label="Диалогов протарифицировано за месяц"
          value={trends.dialogs_billed_month.toLocaleString('ru-RU')}
          icon={MessagesSquare}
          demo
          demoLabel="демо · 100 включено/подписка + overflow по брэкетам"
        />
      </div>

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
        <Card className="border-dashed bg-muted/40">
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between">
              <CardTitle className="text-base text-muted-foreground">Выручка по месяцам</CardTitle>
              <DemoBadge />
            </div>
          </CardHeader>
          <CardContent>
            <ColumnChart data={revData} format={(n) => formatRub(n)} demo />
            <p className="mt-2 text-[11px] text-muted-foreground/70">
              демо · заполнится по мере истории продаж
            </p>
          </CardContent>
        </Card>

        <Card className="border-dashed bg-muted/40">
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between">
              <CardTitle className="text-base text-muted-foreground">Лиды по неделям</CardTitle>
              <DemoBadge />
            </div>
          </CardHeader>
          <CardContent>
            <Sparkline points={leadsData} demo />
            <div className="mt-2 flex items-center justify-between text-[11px] text-muted-foreground">
              <span>{trends.leads_by_week.map((w) => weekLabel(w.week)).join(' · ')}</span>
              {lastWeek && <span className="font-medium">последняя: {lastWeek.count}</span>}
            </div>
            <p className="mt-1 text-[11px] text-muted-foreground/70">
              демо · данных пока мало, заполнится с потоком лидов
            </p>
          </CardContent>
        </Card>
      </div>
    </div>
  )
}
